import React, {
  createContext,
  forwardRef,
  HTMLAttributes,
  ReactElement,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ListChildComponentProps, VariableSizeList } from "react-window";

// Autocomplete's listbox, virtualized. The location list has 1,100+ entries and
// MUI renders every option each time the list opens and on every keystroke —
// 250-600ms per tap on a mid-range phone, the booking page's worst INP. This draws
// only the rows in view (plus a few either side); scrolling and filtering behave
// as before. Pattern from MUI's "Virtualization" Autocomplete demo.

// Matches MUI's .MuiAutocomplete-option: 6px 16px padding, body1 text, and a
// 48px minimum row height below the "sm" breakpoint (600px).
const OPTION_PADDING_Y = 12;
const OPTION_PADDING_X = 32;
const LINE_HEIGHT = 24;
const LISTBOX_PADDING = 8;
const VISIBLE_ROWS = 8;

const OuterElementContext = createContext<HTMLAttributes<HTMLElement>>({});

const OuterElement = forwardRef<HTMLDivElement>(
  function OuterElement(props, ref) {
    const outerProps = useContext(OuterElementContext);
    return <div ref={ref} {...props} {...outerProps} />;
  }
);

const Row = ({ data, index, style }: ListChildComponentProps) =>
  React.cloneElement(data[index] as ReactElement, {
    style: { ...style, top: (style.top as number) + LISTBOX_PADDING },
  });

const textOf = (item: ReactElement): string => {
  const children = item.props?.children;
  return typeof children === "string" ? children : String(children ?? "");
};

let canvas: HTMLCanvasElement | null = null;
const measure = (text: string, font: string) => {
  canvas = canvas ?? document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) return 0;
  ctx.font = font;
  return ctx.measureText(text).width;
};

export const VirtualListbox = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLElement>
>(function VirtualListbox(props, ref) {
  const { children, ...other } = props;
  const items = React.Children.toArray(children) as ReactElement[];

  const boxRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<VariableSizeList | null>(null);
  const [box, setBox] = useState({ width: 0, font: "", minRow: 48 });

  // Rows wrap when a location name is wider than the list, so size each one
  // from its text at the listbox's real width and font.
  useLayoutEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const cs = getComputedStyle(el);
    setBox({
      width: el.clientWidth,
      font: `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`,
      minRow: window.matchMedia("(min-width: 600px)").matches ? 0 : 48,
    });
  }, []);

  const sizes = useMemo(() => {
    const textWidth = Math.max(box.width - OPTION_PADDING_X, 1);
    return items.map((item) => {
      const lines = box.font
        ? Math.max(1, Math.ceil(measure(textOf(item), box.font) / textWidth))
        : 1;
      return Math.max(box.minRow, lines * LINE_HEIGHT + OPTION_PADDING_Y);
    });
  }, [items, box]);

  useEffect(() => {
    listRef.current?.resetAfterIndex(0, true);
  }, [sizes]);

  const visible = sizes.slice(0, VISIBLE_ROWS).reduce((a, b) => a + b, 0);

  return (
    <div ref={ref}>
      <div ref={boxRef}>
        <OuterElementContext.Provider value={other}>
          <VariableSizeList
            ref={listRef}
            itemData={items}
            itemCount={items.length}
            itemSize={(i) => sizes[i]}
            height={visible + 2 * LISTBOX_PADDING}
            width="100%"
            outerElementType={OuterElement}
            innerElementType="ul"
            overscanCount={5}
          >
            {Row}
          </VariableSizeList>
        </OuterElementContext.Provider>
      </div>
    </div>
  );
});
